import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Table } from "../common/Table";
import { registerHooks } from "../../hooks/handlers/useRegisterHandler";
import { TGeneralHookParams } from "../../types/common";
import {
  beautify,
  detailsCardView,
  downloadThroughAnchorLink,
  returnObject,
} from "../../utils/helpers";
import useGet from "../../hooks/common/useGet";
import { sourceApis } from "../../hooks/handlers/useApiHandler";
import { Button } from "antd";
import styled from "styled-components";
import { Add, FilterListOutlined } from "@mui/icons-material";
import DetailsCard from "../common/DetailsCards";
import { BASE_API_URL } from "../../config";
import { ExportButton } from "../common/Buttons";
import { TableFilter } from "../common/TableFilter";
import { blueColor } from "../styled/Theme";

// interface WrapperProps {
//   fullWidth: boolean;
// }

const RegisterLayoutHandler = (props: any) => {
  const {
    source_id,
    register_status,
    api,
    rootData,
    title,
    fullWidth = true,
    register_params,
  } = props;

  let { id: source, status } = useParams<keyof TGeneralHookParams>();
  if (source_id) source = source_id;
  if (register_status) status = register_status;

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const {
    tableData,
    tableColumns,
    tableTitle,
    formRoute,
    createTitle,
    totalsData,
    tableTotals,
    exportData,
    externalFilters,
    initialFilters,
    customApi,
    customActions,
  } =
    source && registerHooks[source]
      ? registerHooks[source]({ status, ...register_params })
      : [];

  const stableInitialFilters = useMemo(
    () => initialFilters,
    [JSON.stringify(initialFilters)],
  );

  const [filters, setFilter] = useState(() => ({
    page: 1,
    page_size: 10,
    ...stableInitialFilters,
    ...Object.fromEntries(searchParams.entries()),
    ...(status ? { status } : {}),
  }));

  const [isDownloadingFiltered, setIsDownloadingFiltered] = useState(false);

  const { loadData: getRegisterData, data: registerData, loading } = useGet();

  const fetchedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!source) return;
    const apiToUse = api || customApi || sourceApis[source];
    if (!apiToUse) return;
    if (status && filters?.status !== status) return;

    const requestKey = JSON.stringify({ source, status, filters });
    if (fetchedKeyRef.current === requestKey) return;
    fetchedKeyRef.current = requestKey;

    getRegisterData({ api: apiToUse, params: filters });
  }, [source, status, api, customApi, filters, getRegisterData]);

  useEffect(() => {
    fetchedKeyRef.current = null;
    const urlParams = Object.fromEntries(searchParams.entries());
    setFilter((prev) => {
      const next = {
        page: 1,
        page_size: 10,
        ...stableInitialFilters,
        ...urlParams,
        ...(status ? { status } : {}),
      };
      return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
    });
  }, [searchParams, status, stableInitialFilters]);

  const onFilterChange = (key: string, value: string | number) => {
    const updatedFilters = { ...filters, page: 1 };
    if (value === "" || value === null || value === undefined) {
      delete updatedFilters[key];
    } else {
      updatedFilters[key] = value;
    }
    setFilter(updatedFilters);
    const apiToUse = api || customApi || (source && sourceApis[source]);
    if (!apiToUse) return;
    getRegisterData({ api: apiToUse, params: updatedFilters });
  };

  const handleTableFilterChange = (newFilters: any) => {
    setFilter(newFilters);
    const apiToUse = api || customApi || (source && sourceApis[source]);
    if (!apiToUse) return;
    getRegisterData({ api: apiToUse, params: newFilters });
  };

  const getTitle = () =>
    `${tableTitle || beautify(source)} ${filters?.stringValue || "Register"} `;

  // Some APIs paginate as { results, count }, others as { items, totalCount }
  let tableDataResults =
    registerData?.results || registerData?.items || registerData || [];
  if (rootData) {
    tableDataResults =
      registerData?.results || registerData?.items || registerData || [];
  }
  const totalCount = registerData?.count ?? registerData?.totalCount;

  const handleDownload = (filtered: boolean = false) => {
    if (filtered) setIsDownloadingFiltered(true);
    downloadThroughAnchorLink(
      `${BASE_API_URL}${exportData?.api}`,
      exportData?.fileName,
      returnObject(filtered, filters),
    ).finally(() => {
      if (filtered) setIsDownloadingFiltered(false);
    });
  };

  const hasActions = formRoute || (customActions && customActions.length > 0);
  const hasExternalFilters = externalFilters && externalFilters.length > 0;

  return (
    <PageWrapper $fullWidth={fullWidth}>
      {/* ── Top toolbar ── */}
      {(hasActions || hasExternalFilters) && (
        <Toolbar>
          {/* Action buttons */}
          {hasActions && (
            <ToolbarActions>
              {formRoute && (
                <PrimaryActionBtn
                  icon={<Add fontSize="small" />}
                  type="primary"
                  onClick={() => navigate(formRoute)}
                  className="capitalize"
                >
                  {createTitle || `New ${beautify(source)}`}
                </PrimaryActionBtn>
              )}
              {customActions?.map((action: any, index: number) => (
                <PrimaryActionBtn
                  key={index}
                  className="capitalize"
                  icon={action.icon}
                  type={action.type || "default"}
                  onClick={action.onClick}
                  {...action.props}
                >
                  {action.label}
                </PrimaryActionBtn>
              ))}
            </ToolbarActions>
          )}

          {/* External filters */}
          {hasExternalFilters && (
            <ExternalFiltersBar>
              <FilterIcon>
                <FilterListOutlined sx={{ fontSize: 15 }} />
                Filters
              </FilterIcon>
              {externalFilters.map((row: any) => (
                <ExternalFilterItem key={row.id}>
                  <TableFilter
                    name={row.id}
                    onChange={onFilterChange}
                    filters={filters}
                    updateFilterValues={setFilter}
                    selectOptions={row?.options}
                    {...row}
                    showHeaders={true}
                  />
                </ExternalFilterItem>
              ))}
            </ExternalFiltersBar>
          )}
        </Toolbar>
      )}

      {/* ── Totals / summary cards ── */}
      {totalsData && (
        <SummaryCardWrapper>
          <DetailsCard
            viewData={detailsCardView(totalsData(registerData))}
            horizontal={true}
          />
        </SummaryCardWrapper>
      )}

      {/* ── Main table ── */}
      <Table
        loading={loading}
        count={totalCount}
        tableTitle={title || getTitle()}
        tableColumns={tableColumns ? tableColumns(tableDataResults) : []}
        tableData={
          tableData ? tableData(tableDataResults, getRegisterData) : []
        }
        tableTotals={tableTotals ? tableTotals(registerData) : {}}
        filters={filters}
        onFilterChange={handleTableFilterChange}
        status={status}
        tableHeaderItem={
          exportData &&
          Object.keys(exportData).length > 0 && (
            <ExportButton
              text="Export"
              title="Export Table Data"
              isDownloading={isDownloadingFiltered}
              onClick={() => handleDownload(true)}
            />
          )
        }
      />
    </PageWrapper>
  );
};

export default RegisterLayoutHandler;

/* ─── Styled Components ─────────────────────────────────────────── */

const PageWrapper = styled.div<{ $fullWidth: boolean }>`
  padding: ${({ $fullWidth }) => ($fullWidth ? "0 24px" : "0")};
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const ToolbarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

export const PrimaryActionBtn = styled(Button)`
  && {
    background: linear-gradient(135deg, ${blueColor} 0%, #1455a0 100%);
    border: none;
    border-radius: 8px;
    height: 32px;
    font-size: 12.5px;
    font-weight: 600;
    letter-spacing: 0.1px;
    color: #fff;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 0 16px;
    box-shadow: 0 2px 8px ${blueColor}40;
    transition: all 0.2s;

    &:hover,
    &:focus {
      background: linear-gradient(135deg, #1455a0 0%, #0e3d7a 100%) !important;
      border: none !important;
      color: #fff !important;
      box-shadow: 0 4px 14px ${blueColor}50 !important;
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
      box-shadow: 0 2px 6px ${blueColor}30 !important;
    }

    .anticon,
    svg {
      font-size: 14px !important;
    }
  }
`;

const ExternalFiltersBar = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 16px;
  flex-wrap: wrap;
  background: #ffffff;
  border: 1px solid #e8f0f8;
  border-radius: 12px;
  padding: 12px 18px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
`;

const FilterIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11.5px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  white-space: nowrap;
  padding-bottom: 2px;
`;

const ExternalFilterItem = styled.div`
  min-width: 160px;
`;

const SummaryCardWrapper = styled.div`
  border-radius: 12px;
  overflow: hidden;
`;

// import { useEffect, useState, useRef } from "react";
// import { useNavigate, useParams, useSearchParams } from "react-router-dom";
// import { Table } from "../common/Table";
// import { registerHooks } from "../../hooks/handlers/useRegisterHandler";
// import { TGeneralHookParams } from "../../types/common";
// import {
//   beautify,
//   detailsCardView,
//   downloadThroughAnchorLink,
//   returnObject,
// } from "../../utils/helpers";
// import useGet from "../../hooks/common/useGet";
// import { sourceApis } from "../../hooks/handlers/useApiHandler";
// import { Button } from "antd";
// import styled from "styled-components";
// import { Add } from "@mui/icons-material";
// import DetailsCard from "../common/DetailsCards";
// import { BASE_API_URL } from "../../config";
// import { ExportButton } from "../common/Buttons";
// import { TableFilter } from "../common/TableFilter";

// interface WrapperProps {
//   fullWidth: boolean;
// }

// const Wrapper = styled.div<WrapperProps>`
//   padding: ${({ fullWidth }) => (fullWidth ? "0 20px" : "0")};
// `;

// const RegisterLayoutHandler = (props: any) => {
//   const {
//     source_id,
//     register_status,
//     api,
//     rootData,
//     title,
//     fullWidth = true,
//     register_params,
//   } = props;

//   let { id: source, status } = useParams<keyof TGeneralHookParams>();
//   if (source_id) source = source_id;
//   if (register_status) status = register_status;

//   const [searchParams] = useSearchParams();
//   const navigate = useNavigate();

//   // Hooks from registerHooks
//   const {
//     tableData,
//     tableColumns,
//     tableTitle,
//     formRoute,
//     createTitle,
//     totalsData,
//     tableTotals,
//     exportData,
//     externalFilters,
//     initialFilters,
//     customApi,
//     customActions,
//   } =
//     source && registerHooks[source]
//       ? registerHooks[source]({ status, ...register_params })
//       : [];

//   // Initialize filters once
//   const [filters, setFilter] = useState(() => ({
//     ...initialFilters,
//     ...Object.fromEntries(searchParams.entries()),
//     ...(status ? { status } : {}),
//   }));

//   const [isDownloadingFiltered, setIsDownloadingFiltered] = useState(false);

//   const { loadData: getRegisterData, data: registerData, loading } = useGet();

//   // Ref to prevent duplicate fetches for the same request (StrictMode-safe)
//   const fetchedKeyRef = useRef<string | null>(null);

//   // Fetch data when source, status, or filters change
//   useEffect(() => {
//     if (!source) return;

//     const apiToUse = api || customApi || sourceApis[source];
//     if (!apiToUse) return;

//     // Guard: skip if filters are stale — sync effect hasn't caught up yet.
//     // This prevents fetching with mismatched params when status changes on navigation.
//     if (status && filters?.status !== status) return;

//     const requestKey = JSON.stringify({ source, status, filters });

//     // Prevent duplicate calls for the same request
//     if (fetchedKeyRef.current === requestKey) return;
//     fetchedKeyRef.current = requestKey;

//     getRegisterData({
//       api: apiToUse,
//       params: filters,
//     });
//   }, [source, status, api, customApi, filters, getRegisterData]);

//   // Sync URL search params into filters — replace (not merge) so stale keys are removed
//   useEffect(() => {
//     // Reset fetchedKeyRef to force refetch when route/status changes
//     fetchedKeyRef.current = null;

//     const urlParams = Object.fromEntries(searchParams.entries());
//     setFilter((prev) => {
//       const next = {
//         ...initialFilters,
//         ...urlParams,
//         ...(status ? { status } : {}),
//       };
//       return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
//     });
//   }, [searchParams, status, initialFilters]);

//   // Handle filter change from individual filters (key-value pairs)
//   const onFilterChange = (key: string, value: string | number) => {
//     const updatedFilters = { ...filters, page: 1 };
//     if (value === "" || value === null || value === undefined) {
//       delete updatedFilters[key];
//     } else {
//       updatedFilters[key] = value;
//     }
//     setFilter(updatedFilters);

//     // Fetch data for updated filters
//     const apiToUse = api || customApi || (source && sourceApis[source]);
//     if (!apiToUse) return;

//     getRegisterData({
//       api: apiToUse,
//       params: updatedFilters,
//     });
//   };

//   // Handle filter change from Table component (receives full filter object)
//   const handleTableFilterChange = (newFilters: any) => {
//     setFilter(newFilters);

//     // Fetch data for updated filters
//     const apiToUse = api || customApi || (source && sourceApis[source]);
//     if (!apiToUse) return;

//     getRegisterData({
//       api: apiToUse,
//       params: newFilters,
//     });
//   };

//   // Compute table title
//   const getTitle = () => {
//     return `${tableTitle || beautify(source)} ${
//       filters?.stringValue || "Register"
//     } `;
//   };

//   // Data for table
//   let tableDataResults = registerData?.results || registerData || [];
//   if (rootData) {
//     tableDataResults = registerData?.results || registerData || [];
//   }

//   // Handle export
//   const handleDownload = (filtered: boolean = false) => {
//     if (filtered) setIsDownloadingFiltered(true);

//     downloadThroughAnchorLink(
//       `${BASE_API_URL}${exportData?.api}`,
//       exportData?.fileName,
//       returnObject(filtered, filters),
//     ).finally(() => {
//       if (filtered) setIsDownloadingFiltered(false);
//     });
//   };

//   return (
//     <Wrapper fullWidth={fullWidth}>
//       {(formRoute || (customActions && customActions.length > 0)) && (
//         <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
//           {formRoute && (
//             <Button
//               className="capitalize"
//               icon={<Add fontSize="small" />}
//               type="primary"
//               // style={{ marginBottom: 20 }}
//               onClick={() => navigate(formRoute)}
//             >
//               {createTitle || `New ${beautify(source)}`}
//             </Button>
//           )}
//           {customActions &&
//             customActions.map((action: any, index: number) => (
//               <Button
//                 key={index}
//                 className="capitalize"
//                 icon={action.icon}
//                 type={action.type || "default"}
//                 onClick={action.onClick}
//                 {...action.props}
//               >
//                 {action.label}
//               </Button>
//             ))}
//         </div>
//       )}

//       {externalFilters && externalFilters.length > 0 && (
//         <div style={{ display: "flex", gap: 30, marginBottom: 30 }}>
//           {externalFilters.map((row) => (
//             <TableFilter
//               key={row.id}
//               name={row.id}
//               onChange={onFilterChange}
//               filters={filters}
//               updateFilterValues={setFilter}
//               selectOptions={row?.options}
//               {...row}
//               showHeaders={true}
//             />
//           ))}
//         </div>
//       )}

//       {totalsData && (
//         <DetailsCard
//           viewData={detailsCardView(totalsData(registerData))}
//           horizontal={true}
//         />
//       )}

//       <Table
//         loading={loading}
//         count={registerData?.count}
//         tableTitle={title || getTitle()}
//         tableColumns={tableColumns ? tableColumns(tableDataResults) : []}
//         tableData={
//           tableData ? tableData(tableDataResults, getRegisterData) : []
//         }
//         tableTotals={tableTotals ? tableTotals(registerData) : {}}
//         filters={filters}
//         onFilterChange={handleTableFilterChange}
//         status={status}
//         tableHeaderItem={
//           exportData &&
//           Object.keys(exportData).length > 0 && (
//             <ExportButton
//               text="Export"
//               title="Export Table Data"
//               isDownloading={isDownloadingFiltered}
//               onClick={() => handleDownload(true)}
//             />
//           )
//         }
//       />
//     </Wrapper>
//   );
// };

// export default RegisterLayoutHandler;
